import type { PipControlState, PipOpenOptions, PipPresence, PipTracks } from './interfaces'
import type { PipViewElements } from './pip-view'

import { applyPresence, applyState, applyTracks, buildPipView } from './pip-view'

const PIP_WINDOW_WIDTH = 360
const PIP_WINDOW_HEIGHT = 480

/** Singleton owner of the Document PiP window. Never throws; never stops tracks (doesn't own them). */
class PipWindowManager {
  private pipWindow: Window | null = null
  private view: PipViewElements | null = null
  private onClose: (() => void) | null = null
  private isOpening = false
  private closeDuringOpen = false

  isSupported (): boolean {
    return typeof window !== 'undefined' && !!window.documentPictureInPicture
  }

  isOpen (): boolean {
    return !!this.pipWindow && !this.pipWindow.closed
  }

  async open (options: PipOpenOptions): Promise<boolean> {
    const api = typeof window !== 'undefined' ? window.documentPictureInPicture : undefined
    if (!api) return false

    if (this.isOpening) return false
    this.isOpening = true

    this.close()
    this.closeDuringOpen = false

    try {
      const pipWindow = await api.requestWindow({
        width: PIP_WINDOW_WIDTH,
        height: PIP_WINDOW_HEIGHT,
      })

      if (this.closeDuringOpen) {
        pipWindow.close()

        return false
      }

      this.pipWindow = pipWindow
      this.onClose = options.handlers.onClose
      this.view = buildPipView(pipWindow.document, options)
      applyTracks(this.view, options.tracks)
      applyState(this.view, options.state)
      if (options.presence) applyPresence(this.view, options.presence)
      pipWindow.addEventListener('pagehide', this.handlePageHide)

      return true
    } catch (error) {
      console.warn('[pip] failed to open Document PiP window', error)
      this.close()

      return false
    } finally {
      this.isOpening = false
    }
  }

  close (): void {
    if (this.isOpening) this.closeDuringOpen = true

    const pipWindow = this.pipWindow
    const onClose = this.onClose

    this.pipWindow = null
    this.view = null
    this.onClose = null

    if (!pipWindow) return

    pipWindow.removeEventListener('pagehide', this.handlePageHide)
    if (!pipWindow.closed) {
      pipWindow.close()
    }
    onClose?.()
  }

  updateTracks (tracks: PipTracks): void {
    if (!this.view || !this.isOpen()) return

    applyTracks(this.view, tracks)
  }

  updateState (state: PipControlState): void {
    if (!this.view || !this.isOpen()) return

    applyState(this.view, state)
  }

  updatePresence (presence: PipPresence): void {
    if (!this.view || !this.isOpen()) return

    applyPresence(this.view, presence)
  }

  private readonly handlePageHide = (): void => {
    this.close()
  }
}

export const PipWindow = new PipWindowManager()
